import { type AppSettings } from './app-settings.model';

type Primitive = 'string' | 'number' | 'boolean';

/**
 * Every leaf the app reads, with the type it must hold. Keep in sync with
 * AppSettings — a deploy-time typo here boots a broken app, not a red build.
 */
const REQUIRED_FIELDS: ReadonlyArray<readonly [path: string, type: Primitive]> = [
  ['apiBaseUrl', 'string'],
  ['auth.baseUrl', 'string'],
  ['auth.tokenUrl', 'string'],
  ['auth.clientId', 'string'],
  ['auth.clientSecret', 'string'],
  ['auth.provider', 'string'],
  ['auth.vipUrl', 'string'],
  ['pusher.key', 'string'],
  ['pusher.cluster', 'string'],
  ['stripe.publishableKey', 'string'],
  ['sentry.dsn', 'string'],
  ['sentry.environment', 'string'],
  ['sentry.tracesSampleRate', 'number'],
  ['sentry.replaysSessionSampleRate', 'number'],
  ['boardingSystem.baseUrl', 'string'],
  ['chatlio.widgetId', 'string'],
  ['googleMaps.apiKey', 'string'],
  ['maintenanceMode', 'boolean'],
];

/** Strings allowed to be empty, because empty is a meaningful value. */
const NULLABLE_STRINGS: ReadonlySet<string> = new Set(['sentry.dsn']);

/**
 * Validates the parsed /config.json against AppSettings. Without this a string
 * "false" in maintenanceMode is truthy and locks the whole app into maintenance.
 */
export function parseAppSettings(raw: unknown): AppSettings {
  if (!isRecord(raw)) {
    throw new Error('Invalid /config.json: expected a JSON object.');
  }

  const errors = REQUIRED_FIELDS.flatMap(([path, type]) => checkField(raw, path, type));

  if (errors.length > 0) {
    throw new Error(`Invalid /config.json:\n${errors.map(error => `  - ${error}`).join('\n')}`);
  }

  return raw as unknown as AppSettings;
}

function checkField(root: Record<string, unknown>, path: string, type: Primitive): string[] {
  const segments = path.split('.');
  let cursor: unknown = root;

  for (const [index, segment] of segments.entries()) {
    if (!isRecord(cursor)) {
      return [`${segments.slice(0, index).join('.')} must be an object`];
    }

    if (!(segment in cursor)) {
      return [`${path} is missing`];
    }

    cursor = cursor[segment];
  }

  if (typeof cursor !== type) {
    return [`${path} must be a ${type}, got ${describe(cursor)}`];
  }

  if (type === 'string' && cursor === '' && !NULLABLE_STRINGS.has(path)) {
    return [`${path} must not be empty`];
  }

  if (type === 'number' && !Number.isFinite(cursor)) {
    return [`${path} must be a finite number`];
  }

  return [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function describe(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value === 'string' ? `string ("${value}")` : typeof value;
}
